<!-- State/province field: US-state combobox when country is US, else free-text input. -->

<script lang="ts" setup>
import type { CountryCode } from '@nzyme/i18n/Country.js';
import { useTranslate } from '@nzyme/vue-i18n';
import { useService } from '@nzyme/vue-ioc';
import { useEmitAsProps } from '@nzyme/vue-utils';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import { computed } from 'vue';
import type { PropType } from 'vue';

import { CountryContext } from '@sigma/common/i18n/CountryContext.js';

import ComboboxInput from './ComboboxInput.vue';
import * as l from './StateInput.loc.js';
import { US_STATES } from './StateInputStates.js';
import TextInput, { TextInputEmits, TextInputProps } from './TextInput.vue';

const props = defineProps({
  ...TextInputProps,
  country: String as PropType<CountryCode>,
});

defineEmits(TextInputEmits);
defineSlots<Slots<{ info: undefined }>>();

const emits = useEmitAsProps<typeof TextInputEmits>();

const t = useTranslate();
const countryCtx = useService(CountryContext);

const resolvedCountry = computed(() => props.country ?? countryCtx?.());
const hasStates = computed(() => resolvedCountry.value === 'US');
const stateItems = computed(() => (hasStates.value ? US_STATES.slice() : []));

const resolvedLabel = computed(() => props.label ?? t(l.stateLabel));
const resolvedPlaceholder = computed(() => {
  if (props.placeholder) {
    return props.placeholder;
  }
  return hasStates.value ? t(l.selectState) : t(l.statePlaceholder);
});
</script>

<template>
  <ComboboxInput
    v-if="hasStates"
    v-bind="{ ...emits, ...props }"
    :items="stateItems"
    :label="resolvedLabel"
    :placeholder="resolvedPlaceholder"
  >
    <template #info="{ field }">
      <slot
        name="info"
        :field="field"
      />
    </template>
  </ComboboxInput>

  <TextInput
    v-else
    v-bind="{ ...emits, ...$props }"
    :label="resolvedLabel"
    :placeholder="resolvedPlaceholder"
  >
    <template #info="{ field: innerField }">
      <slot
        name="info"
        :field="innerField"
      />
    </template>
  </TextInput>
</template>
